import type { UserPortfolioChartRangeOptions, UserPortfolioPoint } from '$lib/types/user';
import { getNumBackInUnits, getPastTimestampInMillisecondsDeltaDaysOrMonths } from '$lib/util/util';
import BigNumber from 'bignumber.js';
import { writable, derived, type Writable, type Readable } from 'svelte/store';
import { sumOfAllUserMargin } from '$lib/home/userInteract/store';
import { tokensAllowedForDepositBalance, userBalances } from '$lib/home/store.js';
import { AUTOSWAP_OP, AUTOSWAP_STABLES } from '$lib/settings';
import { nonStablesAllowedForDeposit, stablesAllowedForDeposit } from '$lib/home/scwallet/config';
import type { Token, TokenSymbol } from '$lib/types/tokens';
import { strategyPositions } from '$lib/strategies/store';
import persistentStorage from '$lib/persistentStorage';

export const userPortfolioPoints: Writable<Array<UserPortfolioPoint>> = persistentStorage<
	Array<UserPortfolioPoint>
>('user-portfolio-points', []);
export const loadingUserPortfolioPoints: Writable<boolean> = writable(true);
export const userPortfolioChartRange: Writable<UserPortfolioChartRangeOptions> = writable('ALL');
export const portfolioValue = derived(
	[
		sumOfAllUserMargin,
		userBalances,
		tokensAllowedForDepositBalance,
		AUTOSWAP_STABLES,
		AUTOSWAP_OP,
		strategyPositions
	],
	([
		$sumOfAllUserMargin,
		$userBalances,
		$tokensAllowedForDepositBalance,
		$AUTOSWAP_STABLES,
		$AUTOSWAP_OP,
		$strategyPositions
	]) => {
		if ($sumOfAllUserMargin && $userBalances) {
			let balance = new BigNumber($sumOfAllUserMargin).plus(
				new BigNumber($userBalances?.sUSD?.balanceUSD)
			);
			if ($tokensAllowedForDepositBalance) {
				for (const token in $tokensAllowedForDepositBalance) {
					const currentToken: Token = $tokensAllowedForDepositBalance[token];
					if (token === 'sUSD' || !currentToken.balanceUSD) {
						continue;
					}

					if (!$AUTOSWAP_STABLES && stablesAllowedForDeposit.includes(token as TokenSymbol)) {
						continue;
					}

					if (!$AUTOSWAP_OP && nonStablesAllowedForDeposit.includes(token as TokenSymbol)) {
						continue;
					}
					balance = balance.plus(
						new BigNumber(currentToken.balanceUSD).multipliedBy(
							`1e${18 - parseInt(currentToken.decimals)}`
						)
					);
				}
			}
			$strategyPositions.forEach((position) => {
				if (
					position?.long?.currentDebtSizeUnderlying &&
					position?.long?.currentSupplySizeUnderlying
				) {
					balance = balance.plus(
						new BigNumber(position.long.currentSupplySizeUsd).minus(
							position.long.currentDebtSizeUsd
						)
					);
				}
			});
			return balance.toString();
		} else {
			return '0';
		}
	}
);
export const portfolioChange24H: Readable<string> = derived(
	[userPortfolioPoints, portfolioValue],
	([$userPortfolioPoints, $portfolioValue]) => {
		if ($userPortfolioPoints && $portfolioValue) {
			const oneDayAgo = new Date(getPastTimestampInMillisecondsDeltaDaysOrMonths(1, 0));
			const oneDayAgoPoint = $userPortfolioPoints.find(
				(point) => new Date(point?.timestamp) < oneDayAgo
			);
			if (oneDayAgoPoint) {
				return new BigNumber($portfolioValue)
					.minus(new BigNumber(oneDayAgoPoint.portfolioValue))
					.toString();
			}
		}
		return '0';
	}
);
export const portfolioChange24HPercentage = derived(
	[portfolioValue, portfolioChange24H],
	([$portfolioValue, $portfolioChange24H]) => {
		if ($portfolioValue && $portfolioChange24H) {
			return getNumBackInUnits(
				new BigNumber($portfolioChange24H)
					.div(new BigNumber($portfolioValue))
					.multipliedBy(1e20)
					.toString(),
				18,
				2
			);
		} else {
			return '-';
		}
	}
);
